import { format } from "date-fns"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { ChevronLeftIcon, TrashIcon } from "lucide-react"
import { toast } from "sonner"
import { AppHeader } from "~/components/AppHeader"
import { EntityIdentity } from "~/components/EntityIdentity"
import { answerText } from "~/lib/forms"
import { usePermission } from "~/hooks/usePermission"
import { TeamPermissions } from "~/lib/permissions"
import {
	EmptyState,
	ErrorState,
	LoadingState
} from "~/components/screens/StateScreen"
import { Button } from "~/components/ui/button"
import { FormCsvExport } from "~/components/forms/FormCsvExport"
import { useTeam } from "~/hooks/useTeam"
import { useOrganization } from "~/hooks/useOrganization"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle
} from "~/components/ui/dialog"
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
	TableRowLink
} from "~/components/ui/table"
import { api } from "~/trpc/react"

export default function FormSubmissionsPage() {
	const { team, isLoading, orgSlug } = useTeam()
	const { organization } = useOrganization()
	const { formId = "" } = useParams<{ formId: string }>()
	const navigate = useNavigate()
	const { hasTeamPermission } = usePermission()
	const [searchParams, setSearchParams] = useSearchParams()
	const openSubmissionId = searchParams.get("submission")
	const submissionQuery = api.forms.submission.useQuery(
		{ teamId: team?.id ?? "", formId, submissionId: openSubmissionId ?? "" },
		{ enabled: Boolean(team?.id && formId && openSubmissionId), retry: false }
	)
	const submissionsQuery = api.forms.submissions.useInfiniteQuery(
		{ teamId: team?.id ?? "", formId },
		{
			enabled: Boolean(team?.id && formId),
			getNextPageParam: (page) => page.nextCursor
		}
	)
	const utils = api.useUtils()
	const deleteSubmission = api.forms.deleteSubmission.useMutation({
		onSuccess: async () => {
			setSearchParams({}, { replace: true })
			toast.success("Submission removed")
			await utils.forms.submissions.invalidate({
				teamId: team?.id ?? "",
				formId
			})
		},
		onError: (error) =>
			toast.error(error.message || "Could not remove the submission")
	})
	const fields = submissionQuery.data?.questions ?? []
	const submissions =
		submissionsQuery.data?.pages.flatMap((page) => page.submissions) ?? []
	if (isLoading || submissionsQuery.isLoading) return <LoadingState />
	if (submissionsQuery.error)
		return (
			<ErrorState
				title="Unable to load submissions"
				description={submissionsQuery.error.message}
			/>
		)
	if (!team) return <EmptyState title="Team not found" />
	const openSubmission = submissionQuery.data
	const submitterMember = organization?.members.find(
		(member) => member.userId === openSubmission?.submitter?.id
	)
	return (
		<main className="flex h-full min-h-0 flex-col">
			<AppHeader
				contentClassName="flex items-center gap-2"
				actions={<FormCsvExport teamId={team.id} formId={formId} />}
			>
				<Button
					variant="ghost"
					size="icon"
					aria-label="Back to forms"
					onClick={() => navigate(`/${orgSlug}/${team.slug}/forms`)}
				>
					<ChevronLeftIcon className="size-4" />
				</Button>
				<EntityIdentity kind="team" name={team.name} image={team.logo} />
				<span className="text-text-sub">/</span>
				<span className="truncate font-medium">
					{submissionsQuery.data?.pages[0]?.formName ?? "Submissions"}
				</span>
			</AppHeader>
			<div className="min-h-0 flex-1 overflow-auto px-4 pb-8 sm:px-6">
				{submissions.length ? (
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Submitted</TableHead>
								<TableHead>By</TableHead>
								<TableHead>Answers</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{submissions.map((submission) => (
								<TableRow key={submission.id} linked>
									<TableCell>
										<TableRowLink
											to={`?submission=${submission.id}`}
											onClick={(event) => {
												event.preventDefault()
												setSearchParams({ submission: submission.id })
											}}
										>
											{format(submission.createdAt, "MMM d, yyyy h:mm a")}
										</TableRowLink>
									</TableCell>
									<TableCell>{submission.submitter?.name ?? "—"}</TableCell>
									<TableCell>{submission.answerCount}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				) : (
					<EmptyState
						scope="inline"
						title="No submissions yet"
						description="Submissions appear here as people fill out the form."
					/>
				)}
				{submissionsQuery.hasNextPage && (
					<div className="flex justify-center py-4">
						<Button
							variant="outline"
							pending={submissionsQuery.isFetchingNextPage}
							onClick={() => submissionsQuery.fetchNextPage()}
						>
							Load more
						</Button>
					</div>
				)}
			</div>
			<Dialog
				open={Boolean(openSubmissionId)}
				onOpenChange={(open) => {
					if (!open) setSearchParams({}, { replace: true })
				}}
			>
				<DialogContent>
					<div className="flex items-start justify-between gap-4">
						<DialogHeader className="min-w-0 text-left">
							<DialogTitle>Submission</DialogTitle>
							<DialogDescription>
								{openSubmission
									? format(openSubmission.createdAt, "MMM d, yyyy h:mm a")
									: ""}
							</DialogDescription>
						</DialogHeader>
						{openSubmission?.submitter && (
							<div className="flex min-w-0 max-w-[50%] justify-end">
								<EntityIdentity
									kind="user"
									userId={openSubmission.submitter.id}
									name={openSubmission.submitter.name}
									image={openSubmission.submitter.image}
									lastSeenAt={submitterMember?.user.lastSeenAt}
									member={
										submitterMember
											? undefined
											: {
													...openSubmission.submitter,
													lastSeenAt: null,
													timezone: null,
													teams: [],
													teamRoles: []
												}
									}
								/>
							</div>
						)}
					</div>
					<div className="flex flex-col gap-3">
						{submissionQuery.isLoading && <LoadingState scope="inline" />}
						{submissionQuery.error && (
							<p role="alert" className="text-sm text-destructive">
								{submissionQuery.error.message}
							</p>
						)}
						{openSubmission?.answers.map((answer) => {
							const field = fields.find((f) => f.id === answer.fieldId)
							return (
								<div key={answer.fieldId} className="flex flex-col gap-0.5">
									<p className="text-xs font-medium text-text-sub">
										{field?.label || `Removed question (${answer.fieldId})`}
									</p>
									<p className="text-sm">{answerText(answer.value) || "—"}</p>
								</div>
							)
						})}
						{hasTeamPermission(TeamPermissions.EDIT_FORMS) && (
							<Button
								variant="destructive-ghost"
								pending={deleteSubmission.isPending}
								disabled={!openSubmission}
								icon={<TrashIcon />}
								className="self-start"
								onClick={() =>
									openSubmission &&
									deleteSubmission.mutate({
										teamId: team?.id ?? "",
										formId,
										submissionId: openSubmission.id
									})
								}
							>
								Remove submission
							</Button>
						)}
					</div>
				</DialogContent>
			</Dialog>
		</main>
	)
}
